import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import EmptyImage from "../../../assets/images/icons/empty-certificates.svg";
import PlusImage from "../../../assets/images/icons/plus-03.svg";
import {
  DateInput,
  FileUpload,
  GeneralButton,
  GeneralInput,
  InfiniteScroll,
  Modal,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { FILETYPES, FILETYPESWithoutHeifHeic } from "../../../Constants/fileConstants";
import { usePostData } from "../../../hooks";
import { useToast } from "../../../Services/ToastContext";
import CertificateCard from "./CertificateCard";
import CertificateSkeleton from "./CertificateSkeleton";

const DoctorCertificate = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [certificates, setCertificates] = useState<any[]>([]);

  const addCertificateSchema = z
    .object({
      Name: z.string().nonempty({ message: t("required_field") }),
      Description: z.string().optional(),
      Date: z.string().nonempty({ message: t("required_field") }),
      Authority: z.string().optional(),
      File: z
        .instanceof(File, { message: t("required_field") })
        .refine((val) => val instanceof File, { message: t("required_field") }),
    })
    .superRefine((data, ctx) => {
      if (!data.Name) {
        ctx.addIssue({
          path: ["Name"],
          code: z.ZodIssueCode.custom,
          message: t("required_field"),
        });
      }
    });

  const { mutate: createCertificate, isPending } = usePostData(
    EndPoints.Certificates,
  );

  type AddCertificateFormData = z.infer<typeof addCertificateSchema>;

  const methods = useForm<AddCertificateFormData>({
    resolver: zodResolver(addCertificateSchema),
    defaultValues: { Date: "" },
    mode: "onBlur",
    shouldUnregister: false,
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = methods;

  const onSubmit = (data: AddCertificateFormData) => {
    const formData = new FormData();
    if (data.Name) formData.append("Name", data.Name);
    if (data.Description) formData.append("Description", data.Description);
    formData.append("Date", data.Date);
    if (data.Authority) formData.append("Authority", data.Authority);
    if (data.File instanceof File) {
      formData.append("File", data.File);
    }

    createCertificate(formData, {
      onSuccess: () => {
        showToast(t("added_successfully"), "success");
        reset();
        closeAddCertificateModal();
        queryClient.invalidateQueries({
          queryKey: [EndPoints.GetCertificates],
        });
      },
      onError: (error: any) => {
        showToast(error.message || t("failed_to_add_Certificate"), "error");
      },
    });
  };

  const openAddCertificateModal = () => setIsModalOpen(true);
  const closeAddCertificateModal = () => {
    setIsModalOpen(false);
    reset();
  };

  return (
    <div className="box flex h-full w-full flex-col overflow-y-auto">
      <div className="mb-5 flex flex-wrap justify-between gap-5">
        <h3 className="min-w-48 gap-2 font-bold">{t("doctor_certificate")}</h3>
        {certificates?.length > 0 && (
          <div>
            <GeneralButton
              icon={PlusImage}
              type="button"
              title={t("add_new_certificate")}
              onClick={openAddCertificateModal}
            />
          </div>
        )}
      </div>

      {isModalOpen && (
        <Modal
          header={t("add_certificate")}
          isOpen={isModalOpen}
          onClose={closeAddCertificateModal} 
        >
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex max-h-[73dvh] space-y-5 overflow-y-auto">
                <div className="w-full space-y-5 p-2">
                  <GeneralInput
                    name="Name"
                    label={t("title")}
                    placeholder={t("title")}
                    required
                  />
                  <DateInput
                    name="Date"
                    label={t("certificate_date")}
                    dateConstraint="past"
                    required
                  />
                  <GeneralInput
                    name="Authority"
                    label={t("where_did_you_get_your_certificate")}
                    placeholder={t("where_did_you_get_your_certificate")}
                  />
                  <GeneralInput
                    type="textarea"
                    name="Description"
                    label={t("certificate_details")}
                    placeholder={t("certificate_details")}
                  />
                  <Controller
                    name="File"
                    control={control}
                    render={({ field }) => (
                      <FileUpload
                        accept={FILETYPESWithoutHeifHeic}
                        onFileChange={(file) => field.onChange(file)}
                        file={field.value}
                        onCancel={() => field.onChange(null)}
                        uploadSuccess={field.value instanceof File}
                        errorMessageKey={
                          errors.File ? errors.File.message : undefined
                        }
                        label={t("image_certificate")}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-4">
                <GeneralButton
                  type="button"
                  fontColor="body-text"
                  title={t("cancel")}
                  color="lite"
                  className="!w-32"
                  onClick={closeAddCertificateModal}
                />
                <GeneralButton
                  className="!px-12"
                  type="submit"
                  title={t("add_certificate")}
                  color="blue"
                  isLoading={isPending}
                />
              </div>
            </form>
          </FormProvider>
        </Modal>
      )}

      <div className="grid grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
        <InfiniteScroll
          endpoint={EndPoints.GetCertificates}
          getAllParams={{}}
          Count={6}
          skeletonCount={6}
          renderSkeleton={<CertificateSkeleton />}
          noDataElement={
            <div className="flex flex-col items-center justify-center gap-4 lg:w-[36%]">
              <img alt="empty" src={EmptyImage} />
              <h4 className="text-center text-lg font-bold">
                {t("enter_certificates_message")}
              </h4>
              <GeneralButton
                icon={PlusImage}
                type="button"
                title={t("add_new_certificate")}
                onClick={openAddCertificateModal}
              />
            </div>
          }
          getData={(fetchedData) => setCertificates(fetchedData || [])}
        />

        {certificates &&
          certificates.map((e) => (
            <CertificateCard
              key={e.Id}
              item={e}
              to={"/profile/doctor_certificate/" + e.Id}
            />
          ))}
      </div>
    </div>
  );
};

export default DoctorCertificate;
